import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DataTable } from "@/components/ui/data-table";
import { Loader2, Receipt, Search, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LoanStatusBadge } from "@/components/loans/LoanStatusBadge";
import { useToast } from "@/components/ui/use-toast";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";

// Column definition for the loans table
const columns = [
  {
    accessorKey: "id",
    header: "Loan ID",
  },
  {
    accessorKey: "borrower_name",
    header: "Borrower",
  },
  {
    accessorKey: "amount",
    header: "Amount",
    cell: ({ row }) => {
      const amount = parseFloat(row.getValue("amount"));
      const formatted = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(amount);
      return formatted;
    },
  },
  {
    accessorKey: "interest_rate",
    header: "Interest Rate",
    cell: ({ row }) => `${row.getValue("interest_rate")}%`,
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => <LoanStatusBadge status={row.getValue("status")} />,
  },
  {
    accessorKey: "payment_due_date",
    header: "Next Payment",
  },
  {
    accessorKey: "actions",
    header: "Actions",
    cell: ({ row }) => {
      const navigate = useNavigate();
      return (
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => navigate(`/lender/loans/details/${row.getValue("id")}`)}
        >
          View Details
        </Button>
      );
    },
  },
];

// Mock data for the loans
const mockLoans = [
  {
    id: "LOAN-2023-001",
    borrower_name: "John Doe",
    amount: 25000,
    interest_rate: 4.5,
    status: "active",
    payment_due_date: "2023-11-15",
  },
  {
    id: "LOAN-2023-002",
    borrower_name: "Jane Smith",
    amount: 15000,
    interest_rate: 5.0,
    status: "active",
    payment_due_date: "2023-11-20",
  },
  {
    id: "LOAN-2023-003",
    borrower_name: "Michael Johnson",
    amount: 50000,
    interest_rate: 3.75,
    status: "at_risk",
    payment_due_date: "2023-11-10",
  },
  {
    id: "LOAN-2023-004",
    borrower_name: "Sarah Williams",
    amount: 30000,
    interest_rate: 4.25,
    status: "active",
    payment_due_date: "2023-11-25",
  },
  {
    id: "LOAN-2023-005",
    borrower_name: "David Brown",
    amount: 10000,
    interest_rate: 5.5,
    status: "active",
    payment_due_date: "2023-11-18",
  },
];

const ActiveLoans = () => {
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("all");

  const { data: loans, isLoading } = useQuery({
    queryKey: ['active-loans'],
    queryFn: async () => {
      try {
        // In a real implementation, this would fetch data from Supabase
        // const { data, error } = await supabase
        //   .from('loans')
        //   .select('*')
        //   .eq('status', 'active');
        
        // For demo purposes, we'll return mock data
        return mockLoans;
      } catch (error) {
        console.error('Error fetching loans:', error);
        toast({
          title: "Error",
          description: "Failed to load loans data",
          variant: "destructive",
        });
        return [];
      }
    }
  });

  // Filter loans based on search term and active tab
  const filteredLoans = loans?.filter(loan => {
    const matchesSearch = 
      loan.borrower_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      loan.id.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (activeTab === "all") return matchesSearch;
    if (activeTab === "at_risk") return matchesSearch && loan.status === "at_risk";
    if (activeTab === "active") return matchesSearch && loan.status === "active";
    
    return matchesSearch;
  }) || [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Active Loans</h1>
        <Receipt className="h-8 w-8 text-primary" />
      </div>
      
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search loans..."
            className="pl-8"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <Button variant="outline" className="flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filter
          </Button>
          <Button>+ New Loan</Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-0">
          <CardTitle>
            <Tabs defaultValue="all" onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="all">All Loans</TabsTrigger>
                <TabsTrigger value="active">Active</TabsTrigger>
                <TabsTrigger value="at_risk">At Risk</TabsTrigger>
              </TabsList>
            </Tabs>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredLoans.length > 0 ? (
            <DataTable columns={columns} data={filteredLoans} />
          ) : (
            <div className="text-center py-10">
              <p className="text-muted-foreground">No loans found matching your criteria</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ActiveLoans;
